import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import { calendarColor, calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { Text, UnstyledButton, NativeCheckbox, Label } from '@/shared/ui';
import { Cross1Icon, GearIcon } from '@/shared/ui/radix-icons';

interface MobileCalendarFilterSheetProps {
  calendars: Calendar[];
  activeCalendars: Set<string>;
  onToggleCalendar: (calendarId: string) => void;
  visitsVisible: boolean;
  onToggleVisits: () => void;
  onOpenSettings: () => void;
  onClose: () => void;
  agencyName?: string;
}

/**
 * Mobile bottom-sheet for toggling calendar visibility and accessing settings.
 *
 * @param props Calendar list, active state, and handlers.
 * @returns Rendered filter sheet.
 */
export const MobileCalendarFilterSheet: React.FC<
  MobileCalendarFilterSheetProps
> = ({
  calendars,
  activeCalendars,
  onToggleCalendar,
  visitsVisible,
  onToggleVisits,
  onOpenSettings,
  onClose,
  agencyName,
}) => {
  const { t } = useTranslation('features/agenda');

  return (
    <div data-testid="mobile-calendar-filter-sheet">
      <UnstyledButton
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-1001 bg-[var(--overlay-backdrop)]"
      >
        {null}
      </UnstyledButton>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('mobile.filter_calendars')}
        className="fixed inset-x-0 bottom-0 z-1003 rounded-t-[var(--radius-lg)] bg-[var(--surface-primary)] shadow-[var(--shadow-lg)]"
        style={{
          paddingBottom:
            'calc(var(--bottom-nav-height, 52px) + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-(--space-4) py-(--space-3)">
          <Text
            component="span"
            className="text-(--text-base) font-bold text-[var(--text-primary)]"
          >
            {t('mobile.filter_calendars')}
          </Text>
          <UnstyledButton
            type="button"
            aria-label={t('mobile.close_sheet')}
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-secondary)] transition-colors active:bg-[var(--surface-secondary)]"
          >
            <Cross1Icon width={16} height={16} />
          </UnstyledButton>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-(--space-4) py-(--space-3)">
          <div className="mb-(--space-3) flex flex-col gap-(--space-1)">
            <CalendarToggleRow
              label={t('sidebar.visits')}
              color="#8A5D4F"
              active={visitsVisible}
              onToggle={onToggleVisits}
            />
            {calendars.map((cal) => (
              <CalendarToggleRow
                key={cal.calendar_id}
                label={calendarDisplayName(cal, t, agencyName)}
                color={calendarColor(cal)}
                active={activeCalendars.has(cal.calendar_id ?? '')}
                onToggle={() => {
                  if (cal.calendar_id) onToggleCalendar(cal.calendar_id);
                }}
              />
            ))}
          </div>
        </div>

        <div className="border-t border-[var(--border-subtle)] px-(--space-4) py-(--space-3)">
          <UnstyledButton
            type="button"
            onClick={() => {
              onClose();
              onOpenSettings();
            }}
            className="flex w-full items-center gap-(--space-2) rounded-[var(--radius-md)] px-(--space-3) py-(--space-2-5) text-(--text-small) font-semibold text-[var(--seed-base)] transition-colors active:bg-[var(--surface-secondary)]"
          >
            <GearIcon width={15} height={15} />
            {t('sidebar.manage_calendars')}
          </UnstyledButton>
        </div>
      </div>
    </div>
  );
};

const CalendarToggleRow: React.FC<{
  label: string;
  color: string;
  active: boolean;
  onToggle: () => void;
}> = ({ label, color, active, onToggle }) => (
  <Label className="flex cursor-pointer items-center gap-(--space-3) rounded-[var(--radius-md)] px-(--space-2) py-(--space-2-5) transition-colors active:bg-[var(--surface-secondary)]">
    <NativeCheckbox
      checked={active}
      onChange={onToggle}
      aria-label={label}
      className="sr-only"
    />
    <Text
      component="span"
      aria-hidden="true"
      className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-(--radius-sm) text-(--text-micro) text-[var(--button-primary-text)] transition-opacity"
      style={{ backgroundColor: color, opacity: active ? 1 : 0.25 }}
    >
      {active && '\u2713'}
    </Text>
    <Text
      component="span"
      className="text-(--text-small) font-medium text-[var(--text-primary)]"
    >
      {label}
    </Text>
  </Label>
);
